/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { SearchExpand, SideNav, SideNavSection, SideNavTitle } from '@repo/shared-client/components/blocks'
import {
  InputFormField,
  SliderFormField,
  SwitchFormField,
  TagMultiSelectFormField,
  tagMultiSelectFromTags,
} from '@repo/shared-client/components/fields'
import { Button, ToggleGroup, ToggleGroupItem } from '@repo/shared-client/components/ui'
import { Form, useFormContext } from '@repo/shared-client/form'
import { cn } from '@repo/shared-client/lib/utils'
import SFJoinButton from '@web/src/components/primatives/sf-join-button/sf-join-button'
import { useEventCategoryOptions } from '@web/src/features/event/use-event-category-options'
import { useTranslation } from '@web/src/lib/intl/react'
import {
  FolderOpenIcon,
  FunnelIcon,
  FunnelPlusIcon,
  MapIcon,
  MapPinIcon,
  RotateCcwIcon,
  SearchIcon,
  TagIcon,
} from 'lucide-react'
import { useCallback, useState } from 'react'
import SFInfoPopup from 'src/components/primatives/sf-info-popup/sf-info-popup'
import SFLocationSearchField from 'src/components/primatives/sf-location-search-field/sf-location-search-field'

import type { EventListFilterFormValues } from '../event-list-filter-types'
import { useEventListFilterFields, useEventListFilterForm } from '../use-event-list-filter-form'

export interface EventListSideNavProps {
  tags: Array<{ id: string; name: string }>
  isAuthenticated: boolean
  hasPermission: (permission: string) => boolean
  defaultLocation?: string
  filterDefaults: EventListFilterFormValues
  filterResetValues: EventListFilterFormValues
  onFiltersApply: (values: EventListFilterFormValues) => void
  onReset: () => void
  showMyEvents?: boolean
}

function EventListSideNavFields({
  tags,
  isAuthenticated,
  hasPermission,
  defaultLocation,
  filterResetValues,
  onFiltersApply,
  onReset,
  showMyEvents,
}: Omit<EventListSideNavProps, 'filterDefaults'>) {
  const { t } = useTranslation()
  const { selectOptions: categoryOptions } = useEventCategoryOptions()
  const { reset, setValue } = useFormContext<EventListFilterFormValues>()
  const { triggerFilterChange, distance, handleSearchChange } = useEventListFilterFields(onFiltersApply)
  const [tagMode, setTagMode] = useState<'include' | 'exclude'>('include')

  const handleTagModeChange = useCallback(
    (nextValue: string) => {
      const newMode = nextValue as 'include' | 'exclude'
      if (newMode === tagMode) return
      if (newMode === 'include') {
        setValue('excludeTags', [])
      } else {
        setValue('tags', [])
      }
      setTagMode(newMode)
      triggerFilterChange(newMode === 'include' ? { excludeTags: [] } : { tags: [] })
    },
    [tagMode, setValue, triggerFilterChange]
  )

  const handleResetFilters = useCallback(() => {
    reset(filterResetValues)
    setTagMode('include')
    onReset()
  }, [filterResetValues, onReset, reset])

  const filtersDisabled = !isAuthenticated

  return (
    <SideNav
      className="h-full min-h-0 flex-1"
      collapseAriaLabel={t('Collapse filters')}
      collapsedTitleIcon={<FunnelIcon />}
      expandAriaLabel={t('Expand filters')}
      header={<SideNavTitle description={t('Refine your search')} title={t('Filters')} />}
      togglePosition="top"
    >
      <SideNavSection className="pt-3" icon={<SearchIcon />} label={t('Search')}>
        <SearchExpand
          clearSearchLabel={t('Clear search')}
          debounceTime={300}
          filterControlsDisabled={filtersDisabled}
          inputProps={{ 'aria-label': t('Search events') }}
          minChars={0}
          name="name"
          onSearchChange={handleSearchChange}
          placeholder={t('Search by name...')}
          toggleFiltersLabel={t('Toggle filters')}
        />
      </SideNavSection>

      {isAuthenticated && (
        <SideNavSection className="pt-3" icon={<MapPinIcon />} label={t('Distance Within')}>
          <div className="flex w-full items-center justify-between gap-2 py-2">
            <SliderFormField
              name="distance"
              onValueChangeEnd={() => triggerFilterChange()}
              sliderProps={{
                className: 'flex-1 min-w-0 px-3 [&_[data-slot=slider-track]]:bg-card/80',
                min: 10,
                max: 1000,
              }}
            />
            <span className="min-w-[50px] whitespace-nowrap text-right">
              {distance?.[0] < 1000 ? t('{{value}} km', { value: distance[0] }) : t('ALL')}
            </span>
          </div>
        </SideNavSection>
      )}

      {isAuthenticated && (
        <SideNavSection className="pt-3" icon={<MapIcon />} label={t('Distance From')}>
          {hasPermission('location:change-location') ? (
            <div className={cn('w-full', filtersDisabled && 'pointer-events-none opacity-60')}>
              <SFLocationSearchField
                name="location"
                onLocationChange={() => triggerFilterChange()}
                placeholder={t('Enter starting location')}
              />
            </div>
          ) : (
            <div className="flex w-full items-center justify-center">
              <InputFormField
                disabled
                inputProps={{
                  className: 'bg-muted/50',
                  readOnly: true,
                  value: defaultLocation,
                }}
                name="locationPlaceholder"
              />
              <SFInfoPopup
                description={t('Adjusting this is available to supporters only')}
                iconButtonProps={{ size: 'icon-lg' }}
                title={t('Supporters Only')}
              />
            </div>
          )}
        </SideNavSection>
      )}

      <SideNavSection className="pt-3" icon={<FolderOpenIcon />} label={t('Category')}>
        <TagMultiSelectFormField
          disabled={filtersDisabled}
          name="categories"
          onValueChange={() => triggerFilterChange()}
          onValueChangeTrigger="close"
          options={categoryOptions}
          placeholder={t('All')}
        />
      </SideNavSection>

      <SideNavSection
        className="pt-3"
        icon={<TagIcon />}
        label={t('Tags')}
        labelEnd={
          <ToggleGroup
            className="w-fit"
            disabled={filtersDisabled}
            multiple={false}
            onValueChange={(groupValue) => {
              const next = groupValue[0]
              if (!next) return
              handleTagModeChange(next)
            }}
            size="sm"
            spacing={0}
            value={[tagMode]}
            variant="outline"
          >
            <ToggleGroupItem value="include">{t('Include')}</ToggleGroupItem>
            <ToggleGroupItem value="exclude">{t('Exclude')}</ToggleGroupItem>
          </ToggleGroup>
        }
      >
        <div className="flex w-full flex-col justify-center gap-1">
          {tagMode === 'include' ? (
            <TagMultiSelectFormField
              disabled={filtersDisabled}
              name="tags"
              onValueChange={() => triggerFilterChange()}
              onValueChangeTrigger="close"
              options={tagMultiSelectFromTags(tags)}
              placeholder={t('All')}
            />
          ) : (
            <TagMultiSelectFormField
              disabled={filtersDisabled}
              name="excludeTags"
              onValueChange={() => triggerFilterChange()}
              onValueChangeTrigger="close"
              options={tagMultiSelectFromTags(tags)}
              placeholder={t('None')}
            />
          )}
        </div>
      </SideNavSection>

      <SideNavSection className="pt-3" icon={<FunnelPlusIcon />} label={t('Other Filters')}>
        <div className="flex flex-col gap-3 py-1">
          {isAuthenticated && (
            <SwitchFormField
              disabled={filtersDisabled}
              label={t('Past Events')}
              name="pastEvents"
              onCheckedChange={(checked) => triggerFilterChange({ pastEvents: checked })}
            />
          )}

          {showMyEvents && (
            <SwitchFormField
              disabled={filtersDisabled}
              label={t('Hosting')}
              name="showHosting"
              onCheckedChange={(checked) => triggerFilterChange({ showHosting: checked })}
            />
          )}
        </div>
      </SideNavSection>

      {isAuthenticated ? (
        <SideNavSection hideWhenCollapsed>
          <Button
            className="mt-4 w-full text-muted-foreground"
            onClick={handleResetFilters}
            size="sm"
            type="button"
            variant="ghost"
          >
            <RotateCcwIcon size={14} />
            {t('Reset Filters')}
          </Button>
        </SideNavSection>
      ) : (
        <SideNavSection hideWhenCollapsed>
          <SFJoinButton className="mt-4 w-full" size="sm" />
        </SideNavSection>
      )}
    </SideNav>
  )
}

export default function EventListSideNav({
  tags,
  isAuthenticated,
  hasPermission,
  defaultLocation,
  filterDefaults,
  filterResetValues,
  onFiltersApply,
  onReset,
  showMyEvents,
}: EventListSideNavProps) {
  const formMethods = useEventListFilterForm(filterDefaults)

  return (
    <Form className="flex h-full min-h-0 w-fit flex-col" formMethods={formMethods} suppressHydrationWarning>
      <EventListSideNavFields
        defaultLocation={defaultLocation}
        filterResetValues={filterResetValues}
        hasPermission={hasPermission}
        isAuthenticated={isAuthenticated}
        onFiltersApply={onFiltersApply}
        onReset={onReset}
        showMyEvents={showMyEvents}
        tags={tags}
      />
    </Form>
  )
}
